import { ref } from 'vue';
import { collection, doc, getDoc, setDoc, Timestamp, type DocumentData } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { useAuth } from './useAuth';
import { useToast } from './useToast';

export interface SchoolContent {
  id: string;
  heroEyebrow: string;
  heroTitle: string;
  heroSubtitle: string;
  updatedAt: Date;
  updatedBy: string;
}

const schoolContent = ref<SchoolContent | null>(null);
const loading = ref(false);

export function useSchool() {
  const { user } = useAuth();
  const { success, error } = useToast();

  const schoolCollection = collection(db, 'school');
  const schoolDocId = 'content';

  const fetchSchool = async (): Promise<void> => {
    try {
      loading.value = true;
      const schoolDoc = doc(schoolCollection, schoolDocId);
      const docSnap = await getDoc(schoolDoc);

      if (!docSnap.exists()) {
        await initializeSchool();
        return;
      }

      const data = docSnap.data();
      schoolContent.value = {
        id: docSnap.id,
        heroEyebrow: data.heroEyebrow || '',
        heroTitle: data.heroTitle || '',
        heroSubtitle: data.heroSubtitle || '',
        updatedAt: data.updatedAt?.toDate() || new Date(),
        updatedBy: data.updatedBy || '',
      };
    } catch (err) {
      console.error('Error fetching school content:', err);
      error('Error al cargar la configuración de la escuela', 'Error');
    } finally {
      loading.value = false;
    }
  };

  const updateSchool = async (heroEyebrow: string, heroTitle: string, heroSubtitle: string): Promise<boolean> => {
    if (!user.value) {
      error('Debes estar autenticado para actualizar el contenido', 'Error de autenticación');
      return false;
    }

    try {
      loading.value = true;
      const schoolDoc = doc(schoolCollection, schoolDocId);

      const updateData: DocumentData = {
        heroEyebrow: heroEyebrow.trim(),
        heroTitle: heroTitle.trim(),
        heroSubtitle: heroSubtitle.trim(),
        updatedAt: Timestamp.now(),
        updatedBy: user.value.email ?? '',
      };

      await setDoc(schoolDoc, updateData, { merge: true });

      schoolContent.value = {
        id: schoolDocId,
        heroEyebrow: updateData.heroEyebrow,
        heroTitle: updateData.heroTitle,
        heroSubtitle: updateData.heroSubtitle,
        updatedAt: new Date(),
        updatedBy: user.value.email ?? '',
      };

      success('Página de escuela actualizada', 'Éxito');
      return true;
    } catch (err) {
      console.error('Error updating school content:', err);
      error('Error al actualizar la configuración de la escuela', 'Error');
      return false;
    } finally {
      loading.value = false;
    }
  };

  const initializeSchool = async (): Promise<void> => {
    try {
      const schoolDoc = doc(schoolCollection, schoolDocId);

      const defaultData: DocumentData = {
        heroEyebrow: 'Formación Musical',
        heroTitle: 'Escola de Música',
        heroSubtitle: 'Especialidades en Música e Movemento, Linguaxe Musical e múltiples instrumentos',
        updatedAt: Timestamp.now(),
        updatedBy: 'system',
      };

      await setDoc(schoolDoc, defaultData, { merge: true });

      schoolContent.value = {
        id: schoolDocId,
        heroEyebrow: defaultData.heroEyebrow,
        heroTitle: defaultData.heroTitle,
        heroSubtitle: defaultData.heroSubtitle,
        updatedAt: new Date(),
        updatedBy: 'system',
      };
    } catch (err) {
      console.error('Error initializing school content:', err);
      error('Error al inicializar la configuración de la escuela', 'Error');
    }
  };

  return {
    schoolContent,
    loading,
    fetchSchool,
    updateSchool,
    initializeSchool,
  };
}
